import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnChanges,
  SimpleChanges,
  inject, OnDestroy,
  signal,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { UavPhotoService } from '@6grain/masofa-monolith-api-client/uav/api/uavPhoto.service';
import {
  Region,
  Crop,
  Firm,
} from '@6grain/masofa-monolith-api-client/dictionaries';
import { RegionService } from '@6grain/masofa-monolith-api-client/dictionaries/api/region.service';
import { CropService } from '@6grain/masofa-monolith-api-client/dictionaries/api/crop.service';
import { FirmService } from '@6grain/masofa-monolith-api-client/dictionaries/api/firm.service';
import { UavAddFieldModalComponent } from '../uav-add-field-modal/uav-add-field-modal.component';
import { ServerTranslatePipe } from '@app/utils/pipes/server-translate-pipe';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UavDataTypeService } from '@6grain/masofa-monolith-api-client/dictionaries/api/uavDataType.service';
import { UavCameraTypeService } from '@6grain/masofa-monolith-api-client/dictionaries/api/uavCameraType.service';
import {
  UavDataType,
  UavCameraType,
} from '@6grain/masofa-monolith-api-client/dictionaries';
import { TagSelectorComponent } from '@app/components/tag-selector/tag-selector.component';
import {
  AddUavFieldRelationCommand,
  UAVFlyPath,
  UpdateUavSurveyCommand,
} from '@6grain/masofa-monolith-api-client/uav';
import { UavSurveyFieldRelationDto } from '@6grain/masofa-monolith-api-client/uav/model/uavSurveyFieldRelationDto';
import { GenericSelectorConfig } from '@root/src/app/pages/Ui-Kit/generic-selector/generic-selector.models';
import { ServerTranslateService } from '@app/services/server-translate.service';
import { of, Subject } from 'rxjs';
import { map, catchError, finalize, takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import { resolveUavSurveyTags } from './uav-survey-tags.util';
import { UAV_EDIT_FAILED, uavServerErrorText } from '../uav-upload-modal/uav-upload-file.util';
import { ConfirmationModalComponent } from '../../Ui-Kit/confirmation-modal/confirmation-modal.component';
import {PrimaryButton} from '@components/button/primary-button/primary-button';
import {SecondaryButton} from '@components/button/secondary-button/secondary-button';
import {MsfInput} from '@components/ui/forms/input/input';

interface FieldRowDisplay {
  relationId: string;
  fieldId: string;
  fieldName: string;
  cropName: string;
  firmName: string;
}

interface ExtendedUAVFlyPath extends UAVFlyPath {
  tagIds?: string[];
  tagNames?: string;
  name?: string | null;
}

@Component({
  selector: 'app-uav-edit-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    UavAddFieldModalComponent,
    ServerTranslatePipe,
    TranslateModule,
    TagSelectorComponent,
    ConfirmationModalComponent,
    PrimaryButton,
    SecondaryButton,
    MsfInput,
  ],
  templateUrl: './uav-edit-modal.component.html',
  styleUrls: ['./uav-edit-modal.component.scss'],
})
export class UavEditModalComponent implements OnInit, OnChanges, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  @Input() isVisible: boolean = false;
  @Input() survey: UAVFlyPath | null = null;

  @Output() close = new EventEmitter<void>();
  @Output() updateSuccess = new EventEmitter<void>();

  private fb = inject(FormBuilder);
  private uavPhotoService = inject(UavPhotoService);
  private regionService = inject(RegionService);
  private cropService = inject(CropService);
  private firmService = inject(FirmService);
  private uavDataTypeService = inject(UavDataTypeService);
  private uavCameraTypeService = inject(UavCameraTypeService);
  private serverTranslate = inject(ServerTranslateService);
  private router = inject(Router);
  private translate = inject(TranslateService);

  activeModalTab: 'info' | 'fields' = 'info';
  isAddFieldModalVisible: boolean = false;
  isSaving: boolean = false;
  /** Why the last save was refused, shown in the modal; null when there is nothing to report. */
  readonly saveError = signal<string | null>(null);
  /** Changes when the modal is closed or switched to another survey, so a late answer of an older save is ignored. */
  private saveGeneration = 0;
  isRecalculating: boolean = false;
  isConfirmVisible: boolean = false;

  confirmConfig = {
    title: '',
    message: '',
    action: '' as 'recalculate' | 'deleteRelation',
    data: null as any,
  };

  editForm!: FormGroup;
  nameControl!: FormControl;

  regions: Region[] = [];
  crops: Crop[] = [];
  firms: Firm[] = [];
  uavDataTypes: UavDataType[] = [];
  cameraTypes: UavCameraType[] = [];

  linkedFields: FieldRowDisplay[] = [];
  isLoadingFields: boolean = false;
  tagNameHints: Record<string, string> = {};
  selectedTagIds: string[] = [];

  cropSelectorConfig: GenericSelectorConfig<Crop> = {
    searchField: 'Names',
    displayField: 'names',
    idField: 'id',
    formatLabel: (item) =>
      (this.serverTranslate.translate(item, 'names') as string) || '',
    getItemById: (id) => this.cropService.dictionariesCropGetByIdIdGet(id),
    getItems: (params) =>
      this.cropService.dictionariesCropGetByQueryPost({
        offset: params.offset,
        take: params.take,
        filters: params.filters,
      }),
    getTotalCount: (params) =>
      this.cropService.dictionariesCropGetTotalCountPost({
        filters: params.filters,
      }),
  };

  firmSelectorConfig: GenericSelectorConfig<Firm> = {
    searchField: 'ShortName',
    displayField: 'shortName',
    idField: 'id',
    getItemById: (id) => this.firmService.dictionariesFirmGetByIdIdGet(id),
    getItems: (params) =>
      this.firmService.dictionariesFirmGetByQueryPost({
        offset: params.offset,
        take: params.take,
        filters: params.filters,
      }),
    getTotalCount: (params) =>
      this.firmService.dictionariesFirmGetTotalCountPost({
        filters: params.filters,
      }),
  };

  constructor() {
    this.initForm();
  }

  ngOnInit(): void {
    this.loadDictionaries();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['survey']) {
      this.resetSave();
    }
    if (changes['survey'] && this.survey) {
      this.patchFormValues();
      this.loadRelatedEntities();
    }
  }

  private initForm(): void {
    this.editForm = this.fb.group({
      name: ['', Validators.required],
      regionId: [null],
      originalDate: [null],
      dataTypeId: [null],
      cameraTypeId: [null],
      cropId: [null],
      firmId: [null],
      tagIds: [[]],
      comment: [''],
    });
    this.nameControl = this.editForm.get('name') as FormControl;
  }

  navigateToField(fieldId: string): void {
    if (fieldId) {
      this.onClose();
      this.router.navigate([`/electronic-field-map/fields/${fieldId}/general`]);
    }
  }

  private loadDictionaries(): void {
    this.regionService.dictionariesRegionGetByQueryPost({}).subscribe((res) => {
      this.regions = res || [];
    });
    this.uavDataTypeService
      .dictionariesUavDataTypeGetByQueryPost({})
      .subscribe((res) => {
        this.uavDataTypes = res || [];
      });
    this.uavCameraTypeService
      .dictionariesUavCameraTypeGetByQueryPost({})
      .subscribe((res) => {
        this.cameraTypes = res || [];
      });
    this.cropService
      .dictionariesCropGetByQueryPost({ take: 999 })
      .subscribe((res) => {
        this.crops = res || [];
      });
    this.firmService
      .dictionariesFirmGetByQueryPost({ take: 999 })
      .subscribe((res) => {
        this.firms = res || [];
      });
  }

  onRegionSelected(regionId: string | null): void {
    this.editForm.get('regionId')?.setValue(regionId);
    this.editForm.markAsDirty();
  }

  private patchFormValues(): void {
    if (!this.survey) return;

    const surveyWithTags = this.survey as ExtendedUAVFlyPath;
    const { tagIds, tagNameHints } = resolveUavSurveyTags(surveyWithTags);
    this.tagNameHints = tagNameHints;
    this.selectedTagIds = tagIds;

    this.editForm.patchValue({
      name: surveyWithTags.name || '',
      dataTypeId: this.survey.dataTypeId,
      cameraTypeId: this.survey.cameraTypeId,
      comment: this.survey.comment,
      tagIds,
    });
  }

  onTagIdsChange(tagIds: string[]): void {
    this.selectedTagIds = tagIds;
    this.editForm.patchValue({ tagIds });
    this.editForm.markAsDirty();
  }

  onClose(): void {
    this.resetSave();
    this.close.emit();
    this.isAddFieldModalVisible = false;
    this.selectedTagIds = [];
    this.editForm.reset();
  }

  // Forgets the outcome of a save: the error text, the busy flag, and any answer still on its way.
  private resetSave(): void {
    this.saveGeneration++;
    this.isSaving = false;
    this.saveError.set(null);
  }

  setModalTab(tab: 'info' | 'fields'): void {
    this.activeModalTab = tab;
  }

  onEditSubmit(): void {
    if (this.editForm.invalid || !this.survey) {
      this.editForm.markAllAsTouched();
      return;
    }

    const photoId = this.survey.id;
    if (!photoId) {
      console.error('Ошибка: У редактируемого объекта отсутствует ID.');
      return;
    }

    this.isSaving = true;
    this.saveError.set(null);
    const generation = this.saveGeneration;
    const formValue = this.editForm.value;

    const updateDto: UpdateUavSurveyCommand = {
      id: photoId,
      name: formValue.name,
      comment: formValue.comment,
      dataTypeId: formValue.dataTypeId,
      cameraTypeId: formValue.cameraTypeId,
      tagIds: formValue.tagIds || [],
    };

    this.uavPhotoService
      .uavUavPhotoUpdateSurveyPut(updateDto)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
      next: (updatedPhoto) => {
        this.isSaving = false;
        this.updateSuccess.emit();
        this.onClose();
      },
      error: (err: HttpErrorResponse) => {
        console.error('Error updating the survey:', err);
        if (generation !== this.saveGeneration) {
          return;
        }
        this.isSaving = false;
        this.saveError.set(uavServerErrorText(err, (key) => this.translate.instant(key), UAV_EDIT_FAILED));
      },
    });
  }

  private loadRelatedEntities(): void {
    this.linkedFields = [];
    if (!this.survey || !this.survey.id) {
      return;
    }
    this.isLoadingFields = true;
    this.uavPhotoService
      .uavUavPhotoGetFieldRelationsSurveyIdGet(this.survey.id)
      .pipe(
        map((relations) => (relations ?? []).map((relation) => this.toFieldRow(relation))),
        catchError(() => of([] as FieldRowDisplay[])),
        finalize(() => (this.isLoadingFields = false)),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (results) => {
          this.linkedFields = results;
        },
      });
  }

  private toFieldRow(relation: UavSurveyFieldRelationDto): FieldRowDisplay {
    const crop = this.crops.find((item) => item.id === relation.cropId);
    const firm = relation.firmId ? this.firms.find((item) => item.id === relation.firmId) : undefined;
    const extra = relation as UavSurveyFieldRelationDto & {
      fieldName?: string | null;
      name?: string | null;
      field?: { name?: string | null };
      firmName?: string | null;
      firm?: { shortName?: string | null; name?: string | null };
    };

    return {
      relationId: relation.id || relation.fieldId || '',
      fieldId: relation.fieldId || '',
      fieldName: extra.fieldName || extra.field?.name || extra.name || 'No data',
      cropName: crop ? (this.serverTranslate.translate(crop, 'names') as string) || '—' : '—',
      firmName: extra.firmName || extra.firm?.shortName || extra.firm?.name || firm?.shortName || '—',
    };
  }

  onRecalculateRelations(): void {
    if (!this.survey || !this.survey.id) return;
    this.confirmConfig = {
      title: 'Recalculate Relations',
      message: 'CONFIRM_RECALCULATE_RELATIONS',
      action: 'recalculate',
      data: null,
    };
    this.isConfirmVisible = true;
  }

  deleteFieldRelation(fieldId: string, relationId: string): void {
    if (!this.survey || !this.survey.id) return;
    this.confirmConfig = {
      title: 'Delete Relation',
      message: 'CONFIRM_DELETE_RELATION',
      action: 'deleteRelation',
      data: { fieldId, relationId },
    };
    this.isConfirmVisible = true;
  }

  onConfirmAction(): void {
    if (this.confirmConfig.action === 'recalculate') {
      this.executeRecalculate();
    } else if (this.confirmConfig.action === 'deleteRelation') {
      this.executeDeleteRelation(
        this.confirmConfig.data.fieldId,
        this.confirmConfig.data.relationId
      );
    }
    this.isConfirmVisible = false;
  }

  private executeRecalculate(): void {
    this.isRecalculating = true;
    this.uavPhotoService
      .uavUavPhotoRecalculateSpatialRelationsSurveyIdPost(this.survey!.id!)
      .pipe(
        finalize(() => {
          this.isRecalculating = false;
        })
      )
      .subscribe({
        next: (success) => {
          if (success) {
            this.loadRelatedEntities();
            this.updateSuccess.emit();
          }
        },
        error: (err) => console.error('Ошибка при пересчете связей:', err),
      });
  }

  private executeDeleteRelation(fieldId: string, relationId: string): void {
    this.isLoadingFields = true;
    this.uavPhotoService
      .uavUavPhotoDeleteFieldRelationSurveyIdFieldIdDelete(
        this.survey!.id!,
        fieldId
      )
      .pipe(
        finalize(() => {
          this.isLoadingFields = false;
        })
      )
      .subscribe({
        next: () => {
          this.linkedFields = this.linkedFields.filter(
            (x) => x.relationId !== relationId
          );
          this.updateSuccess.emit();
        },
        error: (err) => console.error('Ошибка при удалении связи:', err),
      });
  }

  closeConfirmModal(): void {
    this.isConfirmVisible = false;
  }

  stopPropagation(event: Event): void {
    event.stopPropagation();
  }

  openAddFieldModal(): void {
    this.isAddFieldModalVisible = true;
  }

  closeAddFieldModal(): void {
    this.isAddFieldModalVisible = false;
  }

  onFieldSaved(fieldData: any): void {
    if (!this.survey || !this.survey.id) return;

    const rawField = fieldData.field;
    const fieldId =
      rawField && typeof rawField === 'object' ? rawField.id : rawField;
    const cropId = fieldData.culture;

    this.isLoadingFields = true;

    const addRelationDto: AddUavFieldRelationCommand = {
      surveyId: this.survey.id,
      fieldId: fieldId,
      cropId: cropId || null,
    };

    this.uavPhotoService
      .uavUavPhotoAddFieldRelationPost(addRelationDto)
      .subscribe({
        next: (newRelationId: string) => {
          if (!(this.survey as any).fieldRelations) {
            (this.survey as any).fieldRelations = [];
          }

          (this.survey as any).fieldRelations.push({
            id: newRelationId,
            fieldId: fieldId,
            cropId: cropId,
            surveyId: this.survey!.id,
          });

          this.loadRelatedEntities();
          this.updateSuccess.emit();
          this.closeAddFieldModal();
        },
        error: (err) => {
          console.error('Ошибка при добавлении поля:', err);
          this.isLoadingFields = false;
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
